
import React, { useState, useMemo } from 'react';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { 
  Plus, Calendar, ArrowLeft, ChevronRight, Trash2, Edit3, Box, 
  LayoutDashboard, Tag, Filter, SortDesc, X, Users2 
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProgramFormModal } from '../features/programs/ProgramFormModal';
import { EngagementFormModal } from '../features/programs/EngagementFormModal';

const { useParams, useNavigate } = ReactRouterDom as any;

const ProgramDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddEngagementOpen, setIsAddEngagementOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortBy, setSortBy] = useState<'date_desc' | 'name_asc'>('date_desc');

  const { data: program, isLoading } = useQuery({
    queryKey: ['program', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('*, program_categories(label), engagements(*, engagement_categories(label), engagement_tag_links(tag:tag_id(label)))')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    }
  });

  const deleteProgram = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('programs').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      navigate('/programs');
      queryClient.invalidateQueries({ queryKey: ['programs'] });
    }
  });

  const filterOptions = useMemo(() => {
    if (!program?.engagements) return { statuses: [], categories: [], audiences: [], years: [], tags: [] };
    const statuses = new Set<string>();
    const categories = new Set<string>();
    const audiences = new Set<string>();
    const years = new Set<string>();
    const tags = new Set<string>();

    program.engagements.forEach((eng: any) => {
      if (eng.status) statuses.add(eng.status);
      if (eng.engagement_categories?.label) categories.add(eng.engagement_categories.label);
      if (eng.audience_types?.label) audiences.add(eng.audience_types.label);
      if (eng.start_date) {
        const year = eng.start_date.split('-')[0];
        years.add(year);
      }
      if (Array.isArray(eng.engagement_tag_links)) {
        eng.engagement_tag_links.forEach((a: any) => {
          if (a.tag?.label) tags.add(a.tag.label);
        });
      }
    });

    return {
      statuses: Array.from(statuses).sort(),
      categories: Array.from(categories).sort(),
      audiences: Array.from(audiences).sort(),
      years: Array.from(years).sort((a, b) => b.localeCompare(a)),
      tags: Array.from(tags).sort()
    };
  }, [program?.engagements]);

  const filteredEngagements = useMemo(() => {
    if (!program?.engagements) return [];
    let result = [...program.engagements];
    if (statusFilter) result = result.filter(e => e.status === statusFilter);
    if (categoryFilter) result = result.filter(e => e.engagement_categories?.label === categoryFilter);
    if (audienceFilter) result = result.filter(e => e.audience_types?.label === audienceFilter);
    if (yearFilter) result = result.filter(e => e.start_date && e.start_date.split('-')[0] === yearFilter);
    if (tagFilter) result = result.filter(e => 
      Array.isArray(e.engagement_tag_links) && 
      e.engagement_tag_links.some((a: any) => a.tag?.label === tagFilter)
    );

    result.sort((a, b) => {
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
      const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
      const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
      return dateB - dateA;
    });
    return result;
  }, [program?.engagements, statusFilter, categoryFilter, audienceFilter, yearFilter, tagFilter, sortBy]);

  if (isLoading) return <div className="p-20 text-center uppercase font-bold text-slate-400 tracking-widest animate-pulse">Synchronizing records...</div>;
  if (!program) return <div className="p-20 text-center uppercase font-bold text-rose-500">Resource not found</div>;

  return (
    <div className="space-y-8 pb-20 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/programs')} className="p-2 hover:bg-white rounded-md border border-transparent hover:border-slate-200 transition-all text-slate-400">
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest leading-none mb-1">Institutional Strategy</p>
            <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight leading-none mb-2">{program.name}</h1>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-900 text-[#FFDB00] text-[8px] font-black uppercase tracking-tighter border border-slate-700 shadow-sm">
                <div className="w-1 h-1 rounded-full bg-[#FFDB00] animate-pulse" />
                PROGRAM
              </span>
              <div className={`px-2.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border ${program.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}>
                {program.is_active ? 'active' : 'archived'}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsEditModalOpen(true)} icon={<Edit3 size={14} />}>Modify</Button>
          <button onClick={() => { if(confirm('Delete program?')) deleteProgram.mutate(); }} className="p-2.5 text-rose-500 bg-white border border-slate-200 rounded-md hover:bg-rose-50 transition-all shadow-sm">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        {program.program_categories?.label && (
          <div className="px-8 py-4 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center gap-8">
             <div className="flex items-center gap-2">
                 <Box size={12} className="text-slate-400" />
                 <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Category:</span>
                 <span className="text-[9px] font-black uppercase text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-200 shadow-sm">{program.program_categories.label}</span>
             </div>
          </div>
        )}

        <div className="p-8 border-b border-slate-100 bg-white">
          <div className="space-y-2">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Description</h3>
            <p className="text-slate-600 font-medium leading-relaxed max-w-4xl text-sm tracking-wide">{program.description || 'No strategic narrative defined.'}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
            <LayoutDashboard size={20} className="text-blue-600" /> engagement portfolio <span className="text-slate-300 ml-1">({filteredEngagements.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 h-10 shadow-sm">
              <SortDesc size={14} className="text-slate-400" />
              <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="bg-transparent text-[9px] font-black uppercase outline-none cursor-pointer pr-2">
                <option value="date_desc">newest first</option>
                <option value="name_asc">name (a-z)</option>
              </select>
            </div>
            <Button onClick={() => setIsAddEngagementOpen(true)} className="h-10 px-4" icon={<Plus size={16} />}>add engagement</Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-1.5 px-3 border-r border-slate-100 pr-4">
             <Filter size={14} className="text-slate-400" />
             <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Filters</span>
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="bg-slate-50 border border-slate-100 rounded px-3 py-1.5 text-[10px] font-bold uppercase outline-none focus:border-blue-500 transition-colors">
            <option value="">all categories</option>
            {filterOptions.categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(statusFilter || categoryFilter || audienceFilter || yearFilter || tagFilter) && (
            <button onClick={() => { setStatusFilter(''); setCategoryFilter(''); setAudienceFilter(''); setYearFilter(''); setTagFilter(''); }} className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-black uppercase text-rose-500 hover:bg-rose-50 rounded transition-colors">
              <X size={12} /> reset
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredEngagements.map((eng: any) => (
            <div key={eng.id} onClick={() => navigate(`/engagements/${eng.id}`)} className="bg-white p-6 border border-slate-200 hover:border-slate-900 rounded-md transition-all group flex items-center justify-between cursor-pointer shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-md flex items-center justify-center group-hover:bg-slate-900 group-hover:text-[#FFDB00] transition-all">
                  <Calendar size={20} />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-bold text-slate-900 uppercase tracking-tight leading-none">{eng.name}</h4>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-900 text-[8px] font-black uppercase border border-slate-200">engagement</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border tracking-widest ${eng.status === 'delivered' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                      {eng.status || 'in development'}
                    </span>
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{eng.tracking_mode.replace(/_/g, ' ')} mode</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isEditModalOpen && <ProgramFormModal program={program} onClose={() => setIsEditModalOpen(false)} />}
      {isAddEngagementOpen && <EngagementFormModal programId={id!} onClose={() => setIsAddEngagementOpen(false)} />}
    </div>
  );
};

export default ProgramDetail;
